import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, List, Tag } from 'antd';
import {
  SearchOutlined, ThunderboltOutlined, EnvironmentOutlined,
  FilterOutlined, CompassOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TRIAGE_ACTIONS } from '../types/triage';
import type { LocationWithCounts, TriageActionDef } from '../types/triage';
import { useCurrentUser } from '../hooks/useCurrentUser';

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  category: string;
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  locations: LocationWithCounts[];
  onSelectLocation: (id: string | null) => void;
  onAction?: (action: TriageActionDef) => void;
  onSetFilter: (key: string, value: string | null) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({
  open, onClose, locations, onSelectLocation, onAction, onSetFilter,
}) => {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<any>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useCurrentUser();
  // UX-024: read current URL state so each filter command can show whether
  // it would no-op ("Sort: Newest (current)") or actually change something.
  // The palette previously announced "Show Open Tickets" even when status
  // was already null/open, which felt buggy.
  const currentStatus = searchParams.get('status');
  const currentSort = searchParams.get('sort');

  const commands = useMemo((): Command[] => {
    const cmds: Command[] = [];

    // Top-level page navigation — Ctrl+K is the operator's preferred nav
    // surface; previously it could only jump to locations/filters within
    // the current page. Added pages match TriageLayout's top nav.
    [
      { id: 'nav-triage', label: 'Go to Triage', path: '/triage' },
      { id: 'nav-issues', label: 'Go to Issues', path: '/issues' },
      { id: 'nav-tasks', label: 'Go to Work Tasks', path: '/tasks' },
      { id: 'nav-actions', label: 'Go to Actions', path: '/actions' },
      // Same admin-gate as the navbar — non-admin users wouldn't be able
      // to render the page anyway (AdminOnlyRoute redirects them), so
      // hiding the command keeps the palette honest about what works.
      ...(isAdmin ? [{ id: 'nav-agent-health', label: 'Go to Agent Health', path: '/agent-health' }] : []),
      { id: 'nav-help', label: 'Go to Help', path: '/help' },
    ].forEach((n) => {
      cmds.push({
        id: n.id,
        label: n.label,
        description: n.path,
        icon: <CompassOutlined />,
        category: 'Navigation',
        action: () => { navigate(n.path); onClose(); },
      });
    });

    // Triage actions
    TRIAGE_ACTIONS.forEach((a) => {
      cmds.push({
        id: `action-${a.key}`,
        label: a.label,
        description: `Triage: ${a.shortLabel} [${a.shortcutKey}]`,
        icon: <ThunderboltOutlined style={{ color: a.color }} />,
        category: 'Actions',
        action: () => { onAction?.(a); onClose(); },
      });
    });

    // Locations
    locations.slice(0, 50).forEach((loc) => {
      cmds.push({
        id: `loc-${loc.location_id}`,
        label: loc.location_name,
        description: `${loc.ticketCounts.total} tickets`,
        icon: <EnvironmentOutlined />,
        category: 'Locations',
        action: () => { onSelectLocation(loc.location_id); onClose(); },
      });
    });

    // Filters. Description carries the current state when this command
    // would be a no-op, so the operator isn't surprised by "nothing
    // happened" after pressing Enter.
    const filterDefs = [
      { id: 'filter-open', label: 'Show Open Tickets', isCurrent: currentStatus == null || currentStatus === 'open', action: () => { onSetFilter('status', null); onClose(); } },
      { id: 'filter-progress', label: 'Show In Progress', isCurrent: currentStatus === 'in_progress', action: () => { onSetFilter('status', 'in_progress'); onClose(); } },
      { id: 'filter-all', label: 'Show All Tickets', isCurrent: currentStatus === 'all', action: () => { onSetFilter('status', 'all'); onClose(); } },
      { id: 'filter-oldest', label: 'Sort: Oldest First', isCurrent: currentSort == null || currentSort === 'oldest', action: () => { onSetFilter('sort', null); onClose(); } },
      { id: 'filter-newest', label: 'Sort: Newest First', isCurrent: currentSort === 'newest', action: () => { onSetFilter('sort', 'newest'); onClose(); } },
      { id: 'filter-priority', label: 'Sort: Priority', isCurrent: currentSort === 'priority', action: () => { onSetFilter('sort', 'priority'); onClose(); } },
    ];
    filterDefs.forEach((f) => {
      cmds.push({
        id: f.id,
        label: f.label,
        action: f.action,
        icon: <FilterOutlined />,
        category: 'Filters',
        description: f.isCurrent ? 'current' : undefined,
      });
    });

    return cmds;
  }, [locations, onAction, onSelectLocation, onSetFilter, onClose, navigate, currentStatus, currentSort, isAdmin]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter((c) => c.label.toLowerCase().includes(q) || c.category.toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => { setSelectedIdx(0); }, [query]);
  useEffect(() => { if (open) { setQuery(''); setSelectedIdx(0); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && filtered[selectedIdx]) { filtered[selectedIdx].action(); }
  }, [filtered, selectedIdx]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} closable={false} width={500}
      styles={{ body: { padding: 0 } }} style={{ top: 80 }}
    >
      <Input
        ref={inputRef}
        prefix={<SearchOutlined />}
        placeholder="Search actions, locations, filters..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        variant="borderless"
        size="large"
        style={{ borderBottom: '1px solid #f0f0f0' }}
      />
      <div style={{ maxHeight: 360, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>No matches</div>
        ) : (
          <List
            dataSource={filtered.slice(0, 20)}
            size="small"
            renderItem={(cmd, i) => (
              <List.Item
                onClick={cmd.action}
                style={{
                  padding: '8px 16px', cursor: 'pointer',
                  background: i === selectedIdx ? '#e6f4ff' : 'transparent',
                }}
              >
                <List.Item.Meta
                  avatar={cmd.icon}
                  title={<span style={{ fontSize: 13 }}>{cmd.label}</span>}
                  description={cmd.description && <span style={{ fontSize: 11 }}>{cmd.description}</span>}
                />
                <Tag style={{ fontSize: 10 }}>{cmd.category}</Tag>
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default CommandPalette;
